/**
 * Asset context (task 164, Stories 42-43, design r10 "Asset context on
 * detail views").
 *
 * What NinjaOne and ConnectSecure know about the machine a detection names,
 * matched by its name without the domain. Shown on detection detail and,
 * once per endpoint, on alert detail (and so on the ticket screen, which
 * embeds the alert). Display only: nothing here changes a score, a playbook
 * outcome or a response decision.
 *
 * A vendor the Client is not mapped to is left out; with neither mapped
 * the panel does not render at all. A name matching several devices shows
 * each; a name matching none says so.
 */

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import {
  getAssetContext,
  type AssetContext,
  type AssetContextDevice,
  type AssetContextVulnAsset,
  type Detection,
} from '@bluesphere/api-client';
import { SEVERITY_COLORS, SEVERITY_TINTS } from '@bluesphere/ui-kit';

import { useMdrToken } from './context.js';
import { absoluteTime, timeAgo } from './format.js';
import { ErrorState, Loading } from './states.js';

/** Endpoint detections only: an identity detection's asset is a person. */
export function namesEndpoint(d: Detection): boolean {
  return Boolean(d.asset_name && (d.device_id || d.device_os || d.machine_type));
}

export function AssetContextPanel({ tenantId, asset }: { tenantId: string; asset: string }) {
  const idToken = useMdrToken();
  const query = useQuery({
    queryKey: ['asset-context', tenantId, asset],
    queryFn: () => getAssetContext(tenantId, asset, idToken),
    enabled: Boolean(idToken),
    staleTime: 5 * 60_000,
  });

  if (query.isPending) {
    return (
      <Shell asset={asset}>
        <Loading height="h-20" />
      </Shell>
    );
  }
  if (query.isError) {
    return (
      <Shell asset={asset}>
        <ErrorState
          title="Could not load asset context"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </Shell>
    );
  }

  const ctx: AssetContext = query.data;
  if (!ctx.ninjaone.connected && !ctx.connectsecure.connected) return null;

  return (
    <Shell asset={asset}>
      <div className="space-y-5">
        {ctx.ninjaone.connected && (
          <VendorSection name="NinjaOne" asOf={ctx.ninjaone.last_complete_at}>
            {ctx.ninjaone.devices.length === 0 ? (
              <NoMatch vendor="NinjaOne" what="device" nameKey={ctx.name_key} />
            ) : (
              <>
                {ctx.ninjaone.devices.length > 1 && (
                  <p className="mb-2 text-xs text-bluesphere-muted">
                    {ctx.ninjaone.devices.length} devices share this name.
                  </p>
                )}
                <div className="space-y-3">
                  {ctx.ninjaone.devices.map((d) => (
                    <DeviceCard key={d.uid} device={d} />
                  ))}
                </div>
              </>
            )}
          </VendorSection>
        )}
        {ctx.connectsecure.connected && (
          <VendorSection name="ConnectSecure" asOf={ctx.connectsecure.last_complete_at}>
            {ctx.connectsecure.assets.length === 0 ? (
              <NoMatch vendor="ConnectSecure" what="asset" nameKey={ctx.name_key} />
            ) : (
              <div className="space-y-3">
                {ctx.connectsecure.assets.map((a) => (
                  <VulnCard
                    key={a.asset_id}
                    asset={a}
                    labeled={ctx.connectsecure.assets.length > 1}
                  />
                ))}
              </div>
            )}
          </VendorSection>
        )}
      </div>
      <p className="mt-4 text-[11px] text-bluesphere-muted">
        Inventory context is display only. It never changes a score, a playbook outcome or a
        response.
      </p>
    </Shell>
  );
}

function Shell({ asset, children }: { asset: string; children: ReactNode }) {
  return (
    <section className="bs-card p-5">
      <h2 className="text-xl font-bold lg:text-2xl">Asset context</h2>
      <p className="mb-3 break-all text-sm text-bluesphere-muted">{asset}</p>
      {children}
    </section>
  );
}

function VendorSection({
  name,
  asOf,
  children,
}: {
  name: string;
  asOf: string | null;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-sm font-semibold">{name}</h3>
        <span className="text-xs text-bluesphere-muted" title={asOf ?? undefined}>
          {asOf ? `synced ${timeAgo(asOf)}` : 'not yet synced'}
        </span>
      </div>
      {children}
    </div>
  );
}

function NoMatch({
  vendor,
  what,
  nameKey,
}: {
  vendor: string;
  what: string;
  nameKey: string | null;
}) {
  return (
    <p className="text-sm text-bluesphere-muted">
      {nameKey ? (
        <>
          No {vendor} {what} is named <span className="font-mono">{nameKey}</span>.
        </>
      ) : (
        `This asset has no name ${vendor} could match.`
      )}
    </p>
  );
}

function DeviceCard({ device: d }: { device: AssetContextDevice }) {
  const online = d.offline === false;
  return (
    <div className="rounded-lg border border-bluesphere-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="break-all font-semibold">{d.system_name ?? d.uid}</div>
          {d.display_name && d.display_name !== d.system_name && (
            <div className="break-all text-xs text-bluesphere-muted">{d.display_name}</div>
          )}
        </div>
        <span
          className={`bs-chip ${
            online
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-bluesphere-surfaceMuted text-bluesphere-textOnLight'
          }`}
        >
          {d.offline === null ? 'Unknown' : online ? 'Online' : 'Offline'}
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <Row
          label="Last contact"
          value={timeAgo(d.last_contact)}
          title={absoluteTime(d.last_contact)}
        />
        <Row label="OS" value={d.os ?? '—'} />
        <Row label="Serial" value={d.serials.length ? d.serials.join(', ') : '—'} />
        {d.dns_name && <Row label="DNS name" value={d.dns_name} />}
      </dl>
      {d.console_url && (
        <a
          href={d.console_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-xs font-semibold text-bluesphere-primary hover:underline"
        >
          Open in NinjaOne ↗
        </a>
      )}
    </div>
  );
}

const COUNT_ORDER = ['critical', 'high', 'medium', 'low'] as const;

function title(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}

function percent(p: string | null): string {
  const n = Number(p);
  return p && Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';
}

function VulnCard({ asset: a, labeled }: { asset: AssetContextVulnAsset; labeled: boolean }) {
  return (
    <div className="rounded-lg border border-bluesphere-border p-3">
      {labeled && (
        <div className="mb-2 break-all font-semibold">{a.host_name ?? a.name ?? a.asset_id}</div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {COUNT_ORDER.map((sev) => (
          <span
            key={sev}
            className="bs-chip tabular-nums"
            style={{
              color: SEVERITY_COLORS[title(sev)],
              backgroundColor: SEVERITY_TINTS[title(sev)],
            }}
          >
            {a.counts?.[sev] ?? 0} {sev}
          </span>
        ))}
        <span
          className={`bs-chip tabular-nums ${
            (a.kev_count ?? 0) > 0
              ? 'bg-red-700 text-white'
              : 'bg-bluesphere-surfaceMuted text-bluesphere-textOnLight'
          }`}
          title="Known exploited vulnerabilities (CISA KEV)"
        >
          {a.kev_count ?? 0} KEV
        </span>
      </div>
      <p className="mt-2 text-xs text-bluesphere-muted" title={absoluteTime(a.last_discovered)}>
        Last scanned {timeAgo(a.last_discovered)}
        {a.os ? ` · ${a.os}` : ''}
      </p>
      {a.top.length > 0 && (
        <ul className="mt-3 divide-y divide-bluesphere-border text-sm">
          {a.top.map((v) => (
            <li key={`${v.cve}-${v.software}`} className="py-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span
                  className="break-all font-mono text-xs font-semibold"
                  style={{ color: SEVERITY_COLORS[title(v.severity)] }}
                >
                  {v.cve}
                </span>
                <span className="text-xs tabular-nums text-bluesphere-muted">
                  CVSS {v.cvss} · EPSS {percent(v.epss)}
                </span>
              </div>
              {v.software && <div className="break-all text-xs">{v.software}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, value, title: hint }: { label: string; value: string; title?: string }) {
  return (
    <div className="min-w-0" title={hint}>
      <dt className="text-xs text-bluesphere-muted">{label}</dt>
      <dd className="break-all">{value}</dd>
    </div>
  );
}
